import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import mediaApi from "../api/media.api";
import {
    Box, Button, Grid, useTheme, FormControl, InputAdornment, TextField, Typography, Paper, Container
} from "@mui/material";
import MediaItem from "./MediaItem";
import Genrelist from "./Genre";
import Releaseyear from "./Releaseyear";
import MovieFilter from "./gen_releaseyear";

const mediaTypes = ["movie", "tv", "Genre", "ReleaseYear", "ReleaseYear&Genre"];

const Search = ({ theme, toggleTheme }) => {
    const currentTheme = useTheme();
    const [query, setQuery] = useState("");
    const [mediaType, setMediaType] = useState(mediaTypes[0]);
    const [medias, setMedias] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showMovieDetails, setShowMovieDetails] = useState(false);
    const [selectedMovie, setSelectedMovie] = useState(null);
    const [searchClicked, setSearchClicked] = useState(mediaTypes[0] === "movie");

    const search = useCallback(async (searchQuery) => {
        setLoading(true);
        try {
            const { response } = await mediaApi.search({
                mediaType,
                query: searchQuery,
                page,
            });

            if (response) {
                if (page > 1) setMedias(prevMedias => [...prevMedias, ...response.results]);
                else setMedias(response.results);
            }
        } catch (error) {
            toast.error(error.message);
        }
        setLoading(false);
    }, [mediaType, page]);

    const handleCategoryChange = (selectedCategory) => {
        setMediaType(selectedCategory);
        setShowMovieDetails(false);
        setSearchClicked(selectedCategory !== "Genre" && selectedCategory !== "ReleaseYear" && selectedCategory !== "ReleaseYear&Genre");
        setMedias([]); // Clearing the searched results
    };

    const handleQueryChange = (e) => {
        setQuery(e.target.value);
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (query) {
                setSearchClicked(true);
                setPage(1);
                search(query);
            }
        }, 500); // 500ms debounce time
        return () => clearTimeout(delayDebounceFn);
    }, [query, search]);

    const handleSearch = () => {
        setSearchClicked(true);
        setPage(1);
        search(query);
    };

    const handleLoadMore = () => {
        setPage(prevPage => prevPage + 1);
    };

    const handleMediaItemClick = (media) => {
        setSelectedMovie(media);
        setShowMovieDetails(true);
    };

    return (
        <Container maxWidth="lg" sx={{ padding: '10px' }}>
            <Box className="mb-2 d-flex justify-content-center">
                <Grid container spacing={2} justifyContent="center">
                    {mediaTypes.map((item, index) => (
                        <Grid item key={index}>
                            <Button
                                size="large"
                                variant={mediaType === item ? "contained" : "text"}
                                onClick={() => handleCategoryChange(item)}
                                style={{
                                    color: currentTheme.palette.mode === 'dark' ? (mediaType === item ? "black" : "ghostwhite") : (mediaType === item ? "black" : "ghostwhite"),
                                    backgroundColor: currentTheme.palette.mode === 'dark' ? (mediaType === item ? "white" : "black") : (mediaType === item ? "white" : "black"),
                                    fontWeight: "bold",
                                    border: mediaType === item ? "2px solid white" : "2px solid transparent",
                                    minWidth: "100px", // Adjust the minimum width as needed
                                    margin: "0 0.5rem",
                                }}
                            >
                                {item}
                            </Button>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {searchClicked && mediaType !== "Genre" && mediaType !== "ReleaseYear" && mediaType !== "ReleaseYear&Genre" && (
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                    <TextField
                        placeholder="Search Cinesage"
                        fullWidth
                        autoFocus
                        onChange={handleQueryChange}
                        InputProps={{
                            style: {
                                backgroundColor: theme === 'dark' ? 'black' : 'white',
                                color: theme === 'dark' ? 'white' : 'black',
                            },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Button onClick={handleSearch} variant="contained">Search</Button>
                                </InputAdornment>
                            )
                        }}
                        value={query}
                    />
                </Box>
            )}

            {/* Displaying Genre and ReleaseYear components */}
            {mediaType === "Genre" && (
                <Genrelist />
            )}
            {mediaType === "ReleaseYear" && (
                <Releaseyear />
            )}
            {mediaType === "ReleaseYear&Genre" && (
                <MovieFilter />
            )}

            {/* Display movie details */}
            {showMovieDetails && selectedMovie && (
                <Box sx={{ textAlign: 'center' }}>
                    <img src={`https://image.tmdb.org/t/p/w300${selectedMovie.backdrop_path}`} alt={selectedMovie.title} />
                    <Typography variant="h5">{selectedMovie.title}</Typography>
                    <Typography variant="body1">Trailer: {selectedMovie.title}</Typography>
                    <Typography variant="body1">Cast:</Typography>
                    {selectedMovie.cast.map((actor) => (
                        <Typography key={actor.id} variant="body2">{actor.name}</Typography>
                    ))}
                </Box>
            )}

            {/* Display media items */}
            <Grid container spacing={2}>
                {medias.map((media, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <MediaItem media={media} mediaType={mediaType} onClick={() => handleMediaItemClick(media)} />
                    </Grid>
                ))}
            </Grid>

            {/* Load more button */}
            {medias.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                        onClick={handleLoadMore}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'Load More'}
                    </Button>
                </Box>
            )}
        </Container>
    );
};

export default Search;
